import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { CaretRight, Lightning, Lock, Warning, CheckCircle, ClockCountdown, Copy, Check } from "@phosphor-icons/react";
import ClienteSapInput from "../components/ClienteSapInput";

const getMeses12 = () => {
  const meses = [];
  const now = new Date();
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${cap(d.toLocaleDateString("pt-BR", { month: "long" }))}/${d.getFullYear()}`;
    meses.push({ val, label });
  }
  return meses;
};

const calcDdfInfo = (mesEmbarque, dias) => {
  if (!mesEmbarque || !dias || isNaN(Number(dias)) || Number(dias) <= 0) return null;
  const [ano, mes] = mesEmbarque.split("-").map(Number);
  const dataPagamento = new Date(ano, mes - 1, 1);
  dataPagamento.setDate(dataPagamento.getDate() + Number(dias));
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const prazo = Math.ceil((dataPagamento - hoje) / (1000 * 60 * 60 * 24));
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const mesEmbarqueLabel = `${cap(new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long" }))}/${ano}`;
  const mesPagLabel = `${cap(dataPagamento.toLocaleDateString("pt-BR", { month: "long" }))}/${dataPagamento.getFullYear()}`;
  return { prazo, mesEmbarqueLabel, mesPagLabel };
};

export default function Vendedor() {
  const [quote, setQuote] = useState(null);
  const [prevSpot, setPrevSpot] = useState(null);
  const [flash, setFlash] = useState("");
  const [form, setForm] = useState({ cliente: "", volume_usd: "", data_vencimento: "" });
  const [volumeDisplay, setVolumeDisplay] = useState("");
  const [loading, setLoading] = useState(false);
  const [clienteSap, setClienteSap] = useState(null); // { business_partner, nome, cnpj }
  const [cnpjIeConfirmed, setCnpjIeConfirmed] = useState(null);
  // O estado interno do ClienteSapInput (query, resultados, modo CNPJ/IE) só
  // se limpa remontando o componente — por isso o reset é um ponto único:
  // resetForm() incrementa esta key. Antes havia duas cópias do reset em
  // desacordo, e a do botão Limpar esquecia os campos de CNPJ/IE.
  const [clienteKey, setClienteKey] = useState(0);
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const timerRef = useRef(null);
  const [tipoVencimento, setTipoVencimento] = useState("data_fixa");
  const [ddfMes, setDdfMes] = useState("");
  const [ddfDias, setDdfDias] = useState("");
  const [intervalMs, setIntervalMs] = useState(40000);
  const [progress, setProgress] = useState(100);
  const [quoteTick, setQuoteTick] = useState(0);
  const lastUpdateRef = useRef(null);
  const intervalMsRef = useRef(40000);
  const flashTimerRef = useRef(null);
  const updatingTimerRef = useRef(null);

  const loadQuote = async () => {
    try {
      const { data } = await api.get("/cotacao/spot");
      setQuote((old) => {
        if (old && data.spot !== old.spot) {
          setFlash(data.spot > old.spot ? "flash-up" : "flash-down");
          setTimeout(() => setFlash(""), 700);
        }
        return data;
      });
    } catch (e) {
      console.error(e);
    }
  };

  // Busca o intervalo configurado nos parâmetros do sistema
  useEffect(() => {
    let cancelled = false;
    api.get("/parametros/publico").then((r) => {
      if (cancelled) return;
      const ms = r.data?.cotacao_intervalo_ms;
      if (ms && ms > 0) { setIntervalMs(ms); intervalMsRef.current = ms; }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reinicia o polling quando o intervalo muda
  useEffect(() => {
    loadQuoteWithTime();
    timerRef.current = setInterval(loadQuoteWithTime, intervalMs);
    return () => {
      clearInterval(timerRef.current);
      clearTimeout(flashTimerRef.current);
      clearTimeout(updatingTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  // Ticker da barra de progresso (100ms)
  useEffect(() => {
    const tick = setInterval(() => {
      if (!lastUpdateRef.current) return;
      const elapsed = Date.now() - lastUpdateRef.current;
      const pct = Math.max(0, Math.min(100, ((intervalMsRef.current - elapsed) / intervalMsRef.current) * 100));
      setProgress(pct);
    }, 100);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const { volume_usd, data_vencimento } = form;
    if (tipoVencimento === "data_fixa") {
      if (volume_usd && data_vencimento && quote?.spot) {
        api
          .post("/cotacao/calcular", { volume_usd: Number(volume_usd), data_vencimento })
          .then((r) => { if (!cancelled) { setPreview(r.data); } })
          .catch(() => { if (!cancelled) { setPreview(null); } });
      } else {
        setPreview(null);
      }
    } else {
      const info = calcDdfInfo(ddfMes, ddfDias);
      if (volume_usd && info && info.prazo > 0 && quote?.spot) {
        api
          .get("/cotacao/preview", { params: { volume_usd: Number(volume_usd), prazo_dias: info.prazo } })
          .then((r) => { if (!cancelled) { setPreview({ ...r.data, prazo_dias: info.prazo }); } })
          .catch(() => { if (!cancelled) { setPreview(null); } });
      } else {
        setPreview(null);
      }
    }
    return () => { cancelled = true; };
  // form.volume_usd fora das deps de propósito: digitar o Montante não deve
  // recalcular a taxa na hora — o preview só atualiza no próximo tick do
  // countdown (quoteTick), usando o valor mais recente digitado.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.data_vencimento, quoteTick, tipoVencimento, ddfMes, ddfDias]);

  const handleClienteChange = ({ cliente, clienteSap: sap, manualId }) => {
    setClienteSap(sap);
    setCnpjIeConfirmed(manualId);
    setForm(f => ({ ...f, cliente }));
  };

  const resetForm = () => {
    setForm({ cliente: "", volume_usd: "", data_vencimento: "" });
    setVolumeDisplay("");
    setDdfMes("");
    setDdfDias("");
    setTipoVencimento("data_fixa");
    setClienteSap(null);
    setCnpjIeConfirmed(null);
    setClienteKey(k => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente || !form.volume_usd) {
      toast.error("Preencha todos os campos");
      return;
    }
    if (tipoVencimento === "data_fixa" && !form.data_vencimento) {
      toast.error("Preencha a data de vencimento");
      return;
    }
    if (tipoVencimento === "ddf" && (!ddfMes || !ddfDias)) {
      toast.error("Preencha o mês de embarque e os dias DDF");
      return;
    }
    // Captura snapshot da taxa neste exato momento — antes de qualquer await.
    // Taxa e spot saem do MESMO objeto preview (mesma resposta do backend), para
    // que o spread gravado seja coerente: taxa_final = spot + spread.
    const taxaSnapshot = preview?.taxa_final ?? null;
    const spotSnapshot = preview?.spot ?? null;
    setLoading(true);
    try {
      let payload;
      const sapBp = clienteSap?.business_partner || null;
      const sapBps = clienteSap?.business_partners || null;
      if (tipoVencimento === "data_fixa") {
        payload = {
          cliente: form.cliente,
          volume_usd: Number(form.volume_usd),
          data_vencimento: form.data_vencimento,
          tipo_vencimento: "data_fixa",
          sap_business_partner: sapBp,
          business_partners: sapBps,
          taxa_snapshot: taxaSnapshot,
          spot_snapshot: spotSnapshot,
        };
      } else {
        const info = calcDdfInfo(ddfMes, ddfDias);
        if (!info || info.prazo <= 0) {
          toast.error("Prazo DDF inválido");
          setLoading(false);
          return;
        }
        payload = {
          cliente: form.cliente,
          volume_usd: Number(form.volume_usd),
          tipo_vencimento: "ddf",
          ddf_dias: Number(ddfDias),
          ddf_mes_embarque: ddfMes,
          prazo_dias: info.prazo,
          sap_business_partner: sapBp,
          business_partners: sapBps,
          taxa_snapshot: taxaSnapshot,
          spot_snapshot: spotSnapshot,
        };
      }
      const { data } = await api.post("/travas", payload);
      setConfirmation(data);
      toast.success(`Trava ${data.codigo} criada`);
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar trava");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n, dec = 4) => n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const progressColor = (pct) => pct > 60 ? "#1D9E75" : pct > 30 ? "#F5A623" : "#D85A30";

  const [lastUpdate, setLastUpdate] = useState(null);
  const [updating, setUpdating] = useState(false);

  const loadQuoteWithTime = async () => {
    setUpdating(true);
    try {
      const { data } = await api.get("/cotacao/spot");
      setQuote((old) => {
        if (old && data.spot !== old.spot) {
          setFlash(data.spot > old.spot ? "flash-up" : "flash-down");
          clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => setFlash(""), 700);
        }
        return data;
      });
      setLastUpdate(new Date());
      lastUpdateRef.current = Date.now();
      setProgress(100);
      setQuoteTick((t) => t + 1);
    } catch (e) {
      console.error(e);
    } finally {
      clearTimeout(updatingTimerRef.current);
      updatingTimerRef.current = setTimeout(() => setUpdating(false), 500);
    }
  };

  const disabled = !quote?.horario_ok || !quote?.chave_geral_ativa || quote?.divergente;

  const [copied, setCopied] = useState(false);
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (confirmation) {
    return (
      <Layout>
        <div className="p-8 max-w-2xl">
          <div className="border border-[#40B6A2] bg-[#40B6A2]/5 rounded-sm p-8 space-y-6">
            <div className="flex items-center gap-3">
              <CheckCircle weight="fill" className="w-8 h-8 text-[#40B6A2]" />
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#40B6A2]">Trava Confirmada</p>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold font-mono-data cursor-pointer select-none" onClick={() => handleCopy(confirmation.codigo)} title="Copiar código">{copied ? "Copiado!" : confirmation.codigo}</h2>
                  <button
                    type="button"
                    onClick={() => handleCopy(confirmation.codigo)}
                    className="text-[#6b89a8] hover:text-white transition-colors p-1"
                    title="Copiar código"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#40B6A2]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Cliente</p><p className="font-medium">{confirmation.cliente}</p></div>
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Volume</p><p className="font-mono-data">USD {fmt(confirmation.volume_usd, 2)}</p></div>
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Taxa travada</p><p className="font-mono-data text-[#40B6A2] text-lg">R$ {fmt(confirmation.taxa_final)}</p></div>
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider">Prazo</p>
                {confirmation.tipo_vencimento === "ddf" && confirmation.ddf_mes_embarque ? (
                  <p className="font-mono-data text-sm">
                    {(() => {
                      const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
                      const [ano, mes] = confirmation.ddf_mes_embarque.split("-").map(Number);
                      const embarqueLabel = `${cap(new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long" }))}/${ano}`;
                      const pagDate = new Date(ano, mes - 1, 1);
                      pagDate.setDate(pagDate.getDate() + (confirmation.ddf_dias || 0));
                      const pagLabel = `${cap(pagDate.toLocaleDateString("pt-BR", { month: "long" }))}/${pagDate.getFullYear()}`;
                      return `DDF · ${confirmation.ddf_dias} dias · Embarque ${embarqueLabel} · Pagamento est. ${pagLabel}`;
                    })()}
                  </p>
                ) : (
                  <p className="font-mono-data">{confirmation.prazo_dias} dias</p>
                )}
              </div>
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Status</p><StatusBadge status={confirmation.status} /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setConfirmation(null)} data-testid="btn-nova-trava" className="bg-[#40B6A2] text-black hover:bg-[#359a89]">Nova trava</Button>
              <Button variant="outline" onClick={() => window.location.href = "/vendedor/travas"} data-testid="btn-ver-travas">Ver travas pendentes</Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-5xl">
        <div>
          <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Vendedor</p>
          <h1 className="text-3xl font-bold tracking-tight">Cotação & Trava</h1>
        </div>

        {/* Cotação ao vivo — aparece somente quando todos os campos estão preenchidos */}
        {form.cliente && preview && <div className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className={`live-dot ${!quote?.horario_ok || !quote?.chave_geral_ativa ? "opacity-20" : ""}`} />
              <p className="text-xs tracking-[0.2em] uppercase text-[#8aa9c9]">USD / BRL · AO VIVO</p>
            </div>
            <Badge
              className={`bg-transparent border font-mono-data ${
                !quote ? "border-zinc-600 text-[#6b89a8]"
                : !quote.chave_geral_ativa ? "border-[#FF3B30] text-[#FF3B30]"
                : !quote.horario_ok ? "border-[#FFB800] text-[#FFB800]"
                : "border-[#40B6A2] text-[#40B6A2]"
              }`}
              data-testid="badge-status-mercado"
            >
              {!quote ? "CARREGANDO..."
                : !quote.chave_geral_ativa ? "SISTEMA DESATIVADO"
                : !quote.horario_ok ? "FORA DO HORÁRIO"
                : "MERCADO ABERTO"}
            </Badge>
          </div>
          <div className="flex items-baseline gap-4">
            <p className={`text-6xl font-bold font-mono-data ${flash}`} data-testid="text-taxa-vendedor">
              {preview
                ? fmt(preview.taxa_final)
                : quote?.taxa_referencia_acima_corte ? fmt(quote.taxa_referencia_acima_corte) : "—"}
            </p>
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs text-[#6b89a8] font-mono-data">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${updating ? "bg-[#FFB800] animate-pulse" : "bg-[#40B6A2]"}`} />
            <span>{lastUpdate ? `dados atuais · ${lastUpdate.toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit", second:"2-digit"})}` : "carregando..."}</span>
            {preview && <span>· Prazo: {preview.prazo_dias} dias</span>}
          </div>
          {quote?.divergente && (
            <div className="mt-4 border border-[#FF3B30]/40 bg-[#FF3B30]/5 rounded-sm p-3 flex gap-2 text-xs">
              <Warning weight="fill" className="w-4 h-4 text-[#FF3B30]" />
              <span>Divergência entre fontes acima da tolerância. Travas bloqueadas.</span>
            </div>
          )}
          {/* Barra de progresso de atualização */}
          <div className="-mx-6 -mb-6 mt-5 h-[3px] bg-[#061729] rounded-b-sm overflow-hidden">
            <div style={{ width: `${progress}%`, height: "100%", backgroundColor: progressColor(progress), transition: "width 0.1s linear, background-color 0.5s ease" }} />
          </div>
        </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D] space-y-5">
          <h2 className="text-lg font-semibold">Dados da operação</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="cliente" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Cliente</Label>
              <ClienteSapInput
                key={clienteKey}
                value={clienteSap}
                manualId={cnpjIeConfirmed}
                onChange={handleClienteChange}
                allowManual={true}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="volume" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Montante (USD)</Label>
              <Input id="volume" data-testid="input-volume" type="text" inputMode="decimal" value={volumeDisplay}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\./g, "").replace(/[^\d,]/g, "");
                  if (!cleaned) { setVolumeDisplay(""); setForm({ ...form, volume_usd: "" }); return; }
                  const comma = cleaned.indexOf(",");
                  if (comma === -1) {
                    const n = parseInt(cleaned, 10);
                    setVolumeDisplay(isNaN(n) ? "" : n.toLocaleString("pt-BR"));
                    setForm({ ...form, volume_usd: isNaN(n) ? "" : String(n) });
                  } else {
                    const intStr = cleaned.slice(0, comma);
                    const decStr = cleaned.slice(comma + 1).replace(/,/g, "");
                    const n = intStr ? parseInt(intStr, 10) : 0;
                    setVolumeDisplay(`${intStr ? n.toLocaleString("pt-BR") : "0"},${decStr}`);
                    setForm({ ...form, volume_usd: `${intStr || "0"}.${decStr}` });
                  }
                }}
                placeholder="0" className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]" />
              {form.volume_usd && (preview?.taxa_final != null || quote?.taxa_referencia_acima_corte != null) && (
                <p className="text-xs text-[#6b89a8] font-mono-data pt-1">
                  ≈ R${" "}
                  {/* Calculado localmente com o montante digitado agora: o BRL acompanha a
                      digitação usando a última taxa conhecida, enquanto preview.taxa_final
                      (usada na confirmação da trava) só muda no tick do countdown. */}
                  {(Number(form.volume_usd) * (preview?.taxa_final ?? quote.taxa_referencia_acima_corte))
                    .toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <div className="space-y-3 md:col-span-2">
              <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Tipo de Vencimento</Label>
              <div className="flex gap-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "data_fixa"} onChange={() => setTipoVencimento("data_fixa")} data-testid="radio-tipo-data-fixa" className="w-4 h-4 accent-[#40B6A2]" />
                  <span className="text-sm">Data fixa</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "ddf"} onChange={() => setTipoVencimento("ddf")} data-testid="radio-tipo-ddf" className="w-4 h-4 accent-[#40B6A2]" />
                  <span className="text-sm">DDF</span>
                </label>
              </div>
              {tipoVencimento === "data_fixa" && (
                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="dataVenc" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Data de vencimento</Label>
                  <Input id="dataVenc" data-testid="input-data-venc" type="date" value={form.data_vencimento} min={new Date().toISOString().split("T")[0]} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]" />
                </div>
              )}
              {tipoVencimento === "ddf" && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Mês previsto de embarque</Label>
                      <select
                        value={ddfMes}
                        onChange={(e) => setDdfMes(e.target.value)}
                        data-testid="select-ddf-mes"
                        className="w-full bg-[#061729] border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]"
                      >
                        <option value="" className="bg-[#0B2D4D]">Selecione o mês</option>
                        {getMeses12().map(({ val, label }) => (
                          <option key={val} value={val} className="bg-[#0B2D4D]">{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Dias após faturamento (DDF)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={ddfDias}
                        onChange={(e) => setDdfDias(e.target.value)}
                        data-testid="input-ddf-dias"
                        placeholder="Ex: 30"
                        className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]"
                      />
                      <div className="flex gap-2">
                        {[30, 60, 90, 120].map((d) => (
                          <button key={d} type="button" onClick={() => setDdfDias(String(d))} data-testid={`btn-ddf-${d}`}
                            className={`px-2 py-1 text-xs border rounded-sm font-mono-data transition-colors ${ddfDias === String(d) ? "border-[#40B6A2] text-[#40B6A2] bg-[#40B6A2]/10" : "border-[#123a5e] text-[#6b89a8] hover:border-[#40B6A2]/50 hover:text-[#40B6A2]"}`}>
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const info = calcDdfInfo(ddfMes, ddfDias);
                    if (!info) return null;
                    return (
                      <div className="flex items-start gap-2 text-xs text-[#FFB800] bg-[#FFB800]/5 border border-[#FFB800]/20 rounded-sm px-3 py-2">
                        <Warning weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Estimativa: pagamento em <strong>{info.mesPagLabel}</strong> · Prazo para cálculo do spread: <strong>{info.prazo} dias</strong></span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#123a5e] pt-5 space-y-3">
            <div className="text-xs text-[#6b89a8] flex items-center gap-2">
              <Lightning weight="fill" className="w-4 h-4 text-[#FFB800]" />
              Ao confirmar, a taxa é irrevogável
            </div>
            <Button type="submit" disabled={loading || disabled} data-testid="btn-confirmar-trava" className="w-full bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold md:hidden">
              {disabled ? <><Lock className="w-4 h-4 mr-2" /> Bloqueado</> : <>Confirmar trava <CaretRight weight="bold" className="w-4 h-4 ml-2" /></>}
            </Button>
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={resetForm} data-testid="btn-limpar">
                  Cancelar
                </Button>
              </div>
              <Button type="submit" disabled={loading || disabled} data-testid="btn-confirmar-trava" className="hidden md:flex bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
                {disabled ? <><Lock className="w-4 h-4 mr-2" /> Bloqueado</> : <>Confirmar trava <CaretRight weight="bold" className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        </form>

      </div>
    </Layout>
  );
}

function StatusBadge({ status }) {
  const map = {
    pendente_vinculacao: { label: "Pendente vinculação", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    pendente_aprovacao_alcada: { label: "Pend. aprovação alçada", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    vinculada: { label: "Vinculada", color: "bg-[#40B6A2]/10 text-[#40B6A2] border-[#40B6A2]/30" },
    cancelada: { label: "Cancelada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    rejeitada: { label: "Rejeitada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    solicitacao_cancelamento: { label: "Canc. solicitado", color: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
  };
  const c = map[status] || { label: status, color: "border-[#1a4773]" };
  return <Badge className={`${c.color} border font-mono-data text-[10px] tracking-wider uppercase`}>{c.label}</Badge>;
}
